import { useEffect, useState } from "react";
import { UserPlus, Upload, ShieldCheck, ShieldAlert, ShieldQuestion, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { KYC_DOCS_FISICA, KYC_DOCS_MORAL, ROLES_PARTE, ROLES_PARTE_LABELS } from "../../lib/catalogs";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";

const KYC_ESTADO_META = {
    vigente: { badge: "badge-exito", label: "Vigente", Icon: ShieldCheck },
    proximo_vencer: { badge: "badge-gold", label: "Próximo a vencer", Icon: ShieldQuestion },
    vencido: { badge: "badge-mock", label: "Vencido", Icon: ShieldAlert },
    override: { badge: "badge-gold", label: "Override activo", Icon: ShieldCheck },
    sin_documentos: { badge: "badge-mock", label: "Sin documentos", Icon: ShieldQuestion },
};

export default function TabPartes({ fideicomisoId, onReload }) {
    const { hasRole } = useAuth();
    const [partes, setPartes] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newPersonaOpen, setNewPersonaOpen] = useState(false);
    const [addForm, setAddForm] = useState({ persona_id: "", rol: "fideicomitente", grado: "principal" });
    const [expandedParte, setExpandedParte] = useState(null);

    const canPartes = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.ADMIN_TI]);
    const canKyc = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.CUMPLIMIENTO, ROLES.ADMIN_TI]);
    const canOverride = hasRole([ROLES.CUMPLIMIENTO, ROLES.ADMIN_TI]);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: ps }, { data: pers }] = await Promise.all([
                api.get(`/fideicomisos/${fideicomisoId}/partes`),
                api.get("/personas"),
            ]);
            setPartes(ps);
            setPersonas(pers);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fideicomisoId]);

    const addParte = async (e) => {
        e.preventDefault();
        if (!addForm.persona_id) {
            toast.error("Selecciona una persona");
            return;
        }
        try {
            await api.post(`/fideicomisos/${fideicomisoId}/partes`, addForm);
            toast.success("Parte agregada");
            setAddOpen(false);
            setAddForm({ persona_id: "", rol: "fideicomitente", grado: "principal" });
            load();
            onReload?.();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="space-y-6 pb-16">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-display text-2xl">Partes del fideicomiso</h2>
                    <p className="text-sm text-muted-foreground">
                        Fideicomitentes, fideicomisarios, delegados, comité, firmantes.
                    </p>
                </div>
                {canPartes && (
                    <div className="flex gap-2">
                        <NuevaPersonaDialog
                            open={newPersonaOpen}
                            setOpen={setNewPersonaOpen}
                            onCreated={(p) => {
                                setPersonas([p, ...personas]);
                                setAddForm({ ...addForm, persona_id: p.id });
                                setNewPersonaOpen(false);
                                setAddOpen(true);
                            }}
                        />
                        <Dialog open={addOpen} onOpenChange={setAddOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="add-parte-btn">
                                    <UserPlus className="h-4 w-4 mr-2" /> Agregar parte
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Agregar parte al fideicomiso</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={addParte} className="space-y-3">
                                    <div>
                                        <Label>Persona</Label>
                                        <Select
                                            value={addForm.persona_id}
                                            onValueChange={(v) =>
                                                setAddForm({ ...addForm, persona_id: v })
                                            }
                                        >
                                            <SelectTrigger data-testid="select-persona">
                                                <SelectValue placeholder="Seleccionar…" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {personas.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        {p.nombre_display}{" "}
                                                        <span className="text-muted-foreground text-xs">
                                                            · {p.tipo} · {p.rfc || "sin RFC"}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Rol</Label>
                                            <Select
                                                value={addForm.rol}
                                                onValueChange={(v) =>
                                                    setAddForm({ ...addForm, rol: v })
                                                }
                                            >
                                                <SelectTrigger data-testid="select-rol-parte">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ROLES_PARTE.map((r) => (
                                                        <SelectItem key={r} value={r}>
                                                            {ROLES_PARTE_LABELS[r]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Grado</Label>
                                            <Select
                                                value={addForm.grado}
                                                onValueChange={(v) =>
                                                    setAddForm({ ...addForm, grado: v })
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="principal">Principal</SelectItem>
                                                    <SelectItem value="sustituto">Sustituto</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit" data-testid="submit-add-parte">
                                            Agregar
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="partes-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Rol</th>
                            <th>Grado</th>
                            <th>KYC</th>
                            <th>Alertas</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {partes.map((p) => {
                            const meta = KYC_ESTADO_META[p.kyc_estado] || KYC_ESTADO_META.sin_documentos;
                            return (
                                <>
                                    <tr key={p.id} data-testid={`parte-row-${p.id}`}>
                                        <td className="font-medium">{p.persona?.nombre_display}</td>
                                        <td className="text-xs">{p.persona?.tipo}</td>
                                        <td>
                                            <span className="badge-gold">
                                                {ROLES_PARTE_LABELS[p.rol]}
                                            </span>
                                        </td>
                                        <td className="text-xs">{p.grado}</td>
                                        <td>
                                            <span className={meta.badge}>{meta.label}</span>
                                        </td>
                                        <td className="text-xs">
                                            {p.persona?.pep && (
                                                <span className="badge-mock mr-1">PEP</span>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() =>
                                                    setExpandedParte(
                                                        expandedParte === p.id ? null : p.id,
                                                    )
                                                }
                                                data-testid={`toggle-kyc-${p.id}`}
                                            >
                                                {expandedParte === p.id ? "Cerrar" : "KYC"}
                                            </Button>
                                        </td>
                                    </tr>
                                    {expandedParte === p.id && (
                                        <tr key={`${p.id}-kyc`}>
                                            <td colSpan={7} className="bg-muted/40 p-4">
                                                <KycPanel
                                                    parte={p}
                                                    canUpload={canKyc}
                                                    canOverride={canOverride}
                                                    onChange={() => {
                                                        load();
                                                        onReload?.();
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                        {partes.length === 0 && !loading && (
                            <tr>
                                <td colSpan={7} className="text-center py-10 text-muted-foreground">
                                    Sin partes cargadas. Agrega la primera para arrancar el
                                    onboarding.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NuevaPersonaDialog({ open, setOpen, onCreated }) {
    const [tipo, setTipo] = useState("fisica");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({});

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form, tipo };
            const { data } = await api.post("/personas", payload);
            const matches = data.listas_negras_matches || [];
            if (matches.length > 0) {
                toast.warning(
                    `${matches.length} coincidencia(s) en listas negras`,
                    { description: matches.map((m) => `${m.tipo_lista}: ${m.nombre}`).join(", ") },
                );
            } else {
                toast.success("Persona creada");
            }
            onCreated?.(data.persona);
            setForm({});
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-persona-btn">
                    <UserPlus className="h-4 w-4 mr-2" /> Nueva persona
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Alta de persona</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="new-persona-form">
                    <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm cursor-pointer">
                            <input
                                type="radio"
                                checked={tipo === "fisica"}
                                onChange={() => setTipo("fisica")}
                            />
                            Persona física
                        </label>
                        <label className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm cursor-pointer">
                            <input
                                type="radio"
                                checked={tipo === "moral"}
                                onChange={() => setTipo("moral")}
                            />
                            Persona moral
                        </label>
                    </div>
                    {tipo === "fisica" ? (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Nombres</Label>
                                    <Input
                                        required
                                        onChange={(e) =>
                                            setForm({ ...form, nombres: e.target.value })
                                        }
                                    />
                                </div>
                                <div>
                                    <Label>Apellido paterno</Label>
                                    <Input
                                        required
                                        onChange={(e) =>
                                            setForm({ ...form, apellido_paterno: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>RFC</Label>
                                    <Input
                                        onChange={(e) => setForm({ ...form, rfc: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>CURP</Label>
                                    <Input
                                        onChange={(e) => setForm({ ...form, curp: e.target.value })}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <Label>Razón social</Label>
                                <Input
                                    required
                                    onChange={(e) =>
                                        setForm({ ...form, razon_social: e.target.value })
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>RFC</Label>
                                    <Input
                                        onChange={(e) => setForm({ ...form, rfc: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Giro</Label>
                                    <Input
                                        onChange={(e) => setForm({ ...form, giro: e.target.value })}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} data-testid="submit-new-persona">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function KycPanel({ parte, canUpload, canOverride, onChange }) {
    const tipos = parte.persona?.tipo === "moral" ? KYC_DOCS_MORAL : KYC_DOCS_FISICA;
    const [form, setForm] = useState({ tipo_documento: tipos[0], fecha_emision: "", fecha_vencimiento: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ovOpen, setOvOpen] = useState(false);

    const upload = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Selecciona archivo");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("tipo_documento", form.tipo_documento);
            if (form.fecha_emision) fd.append("fecha_emision", form.fecha_emision);
            if (form.fecha_vencimiento) fd.append("fecha_vencimiento", form.fecha_vencimiento);
            fd.append("archivo", file);
            await api.post(`/partes/${parte.id}/kyc/documentos`, fd);
            toast.success("KYC subido");
            setFile(null);
            onChange?.();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const download = async (docId, filename) => {
        try {
            const res = await api.get(`/kyc/documentos/${docId}/download`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Expediente KYC · {parte.persona?.nombre_display}
            </div>

            {parte.kyc_override && (
                <div
                    className="text-xs border rounded-sm p-2"
                    style={{
                        borderColor: "var(--brand-gold)",
                        backgroundColor: "rgba(232,169,60,0.08)",
                    }}
                >
                    <b>Override activo</b> · vence {parte.kyc_override.vigencia_hasta} · otorgado por{" "}
                    {parte.kyc_override.otorgado_por}
                    <div className="text-muted-foreground">
                        Motivo: {parte.kyc_override.motivo}
                    </div>
                </div>
            )}

            <div className="rounded-md border border-border bg-card">
                <table className="trf-table text-xs">
                    <thead>
                        <tr>
                            <th>Documento</th>
                            <th>Emisión</th>
                            <th>Vencimiento</th>
                            <th>Estado</th>
                            <th>Hash</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {parte.kyc_documentos?.map((d) => (
                            <tr key={d.id}>
                                <td>{d.tipo_documento}</td>
                                <td className="tabular">{d.fecha_emision || "—"}</td>
                                <td className="tabular">{d.fecha_vencimiento || "—"}</td>
                                <td>
                                    <span
                                        className={
                                            d.estado === "vencido"
                                                ? "badge-mock"
                                                : d.estado === "proximo_vencer"
                                                  ? "badge-gold"
                                                  : "badge-exito"
                                        }
                                    >
                                        {d.estado}
                                    </span>
                                </td>
                                <td className="hash-cell">{d.hash?.slice(0, 10)}…</td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => download(d.id, d.filename)}
                                    >
                                        <Download className="h-3 w-3" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {(!parte.kyc_documentos || parte.kyc_documentos.length === 0) && (
                            <tr>
                                <td colSpan={6} className="text-center py-4 text-muted-foreground">
                                    Sin documentos aún.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {canUpload && (
                <form
                    onSubmit={upload}
                    className="grid md:grid-cols-5 gap-2 items-end"
                    data-testid={`kyc-upload-form-${parte.id}`}
                >
                    <div>
                        <Label className="text-[10px]">Tipo</Label>
                        <Select
                            value={form.tipo_documento}
                            onValueChange={(v) => setForm({ ...form, tipo_documento: v })}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {tipos.map((t) => (
                                    <SelectItem key={t} value={t}>
                                        {t}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px]">Emisión</Label>
                        <Input
                            type="date"
                            value={form.fecha_emision}
                            onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label className="text-[10px]">Vencimiento</Label>
                        <Input
                            type="date"
                            value={form.fecha_vencimiento}
                            onChange={(e) =>
                                setForm({ ...form, fecha_vencimiento: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <Label className="text-[10px]">Archivo</Label>
                        <Input
                            type="file"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid={`kyc-file-${parte.id}`}
                        />
                    </div>
                    <Button type="submit" disabled={busy} data-testid={`kyc-submit-${parte.id}`}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Upload className="h-3 w-3 mr-1" /> Subir</>)}
                    </Button>
                </form>
            )}

            {canOverride && (
                <Dialog open={ovOpen} onOpenChange={setOvOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`override-btn-${parte.id}`}>
                            Otorgar override
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Override de KYC</DialogTitle>
                        </DialogHeader>
                        <OverrideForm
                            parteId={parte.id}
                            onDone={() => {
                                setOvOpen(false);
                                onChange?.();
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function OverrideForm({ parteId, onDone }) {
    const [motivo, setMotivo] = useState("");
    const [vigencia, setVigencia] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("motivo", motivo);
            fd.append("vigencia_hasta", vigencia);
            if (file) fd.append("archivo", file);
            await api.post(`/partes/${parteId}/kyc/override`, fd);
            toast.success("Override otorgado");
            onDone?.();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div>
                <Label>Motivo</Label>
                <Textarea required value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="ov-motivo" />
            </div>
            <div>
                <Label>Vigencia hasta</Label>
                <Input type="date" required value={vigencia} onChange={(e) => setVigencia(e.target.value)} data-testid="ov-vigencia" />
            </div>
            <div>
                <Label>Documento soporte (opcional)</Label>
                <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="ov-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Otorgar"}
                </Button>
            </DialogFooter>
        </form>
    );
}
